import {Component, inject, computed, signal, effect, DestroyRef} from '@angular/core';
import {CurrencyPipe} from '@angular/common';
import {MatSelectModule} from '@angular/material/select';
import {MatFormFieldModule} from '@angular/material/form-field';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {MatSelectChange} from '@angular/material/select';
import {UserService} from '../../core/user/services/user.service';
import {
  GoodwillModel,
  AssortmentRxModel,
  AssortmentNonRxModel,
  RevenueModel,
  FeesGeneralModel,
  FeeReturnsModel,
  EvaluationSum,
  EvaluationData,
} from './models';
import {EvaluationRestService} from './services/evaluations.rest-service';
import {EvaluationsService} from './services/evaluations.service';
import {ResponseModel} from '../../core/base/response.model';
import {EvaluationsTableComponent, TableColumn} from "./evaluations-table/evaluations-table.component";

@Component({
  selector: 'app-evaluations',
  templateUrl: './evaluations.component.html',
  styleUrl: './evaluations.component.scss',
  standalone: true,
  imports: [
    MatSelectModule,
    MatFormFieldModule,
    EvaluationsTableComponent,
    CurrencyPipe,
  ]
})

export class EvaluationsComponent {
  private readonly userService = inject(UserService);
  private readonly evaluationRestService = inject(EvaluationRestService);
  private readonly evaluationsService = inject(EvaluationsService);
  private readonly destroyRef = inject(DestroyRef);

  private readonly evaluationData = signal<EvaluationData | null>(null);
  readonly selectedMonth = signal<number>(0);
  readonly chosenDetails = signal<string>('');
  readonly isLoading = signal<boolean>(false);
  readonly hasError = signal<boolean>(false);
  readonly months = this.evaluationsService.monthsDetailed;

  readonly goodwill = computed(() => {
    const data = this.evaluationData();
    return data?.goodwill ? new GoodwillModel(data.goodwill) : new GoodwillModel();
  });

  readonly revenue = computed(() => {
    const data = this.evaluationData();
    return data?.revenue ? new RevenueModel(data.revenue) : new RevenueModel();
  });

  readonly assortment = computed(() => ({
    assortment_rx: new AssortmentRxModel(this.evaluationData()?.assortment_rx ?? {}),
    assortment_non_rx: new AssortmentNonRxModel(this.evaluationData()?.assortment_non_rx ?? {})
  }));

  readonly fees = computed(() => ({
    fees_general: new FeesGeneralModel(this.evaluationData()?.fees_general),
    fees_returns: new FeeReturnsModel(this.evaluationData()?.fees_returns)
  }));

  readonly sum = computed(() => this.calculateSums(this.evaluationData()));
  readonly evaluations = computed(() => this.evaluationData());

  readonly goodwillColumns: TableColumn[] = [
    {key: 'label', label: 'Zusammensetzung der Zeile "Kulanzen" in der Vorteilserläuterung'},
    {key: 'value', label: 'Basiswert', type: 'currency'}
  ];

  readonly assortmentRxColumns: TableColumn[] = [
    {key: 'label', label: 'RX-Umsatz Servicesortimente'},
    {key: 'packages', label: 'Pck.', type: 'number'},
    {key: 'revenue', label: 'Basiswert', type: 'currency'}
  ];

  readonly assortmentNonRxColumns: TableColumn[] = [
    {key: 'label', label: 'Non-RX-Umsatz Servicesortimente'},
    {key: 'revenue', label: 'Basiswert', type: 'currency'}
  ];

  readonly revenueColumns: TableColumn[] = [
    {key: 'label', label: 'Gesamtumsatz'},
    {key: 'revenue_pre_returns', label: 'Umsatz vor Retouren', type: 'currency'},
    {key: 'percentage_of_revenue', label: '% vom Umsatz', type: 'percent'},
    {key: 'packages', label: 'Pck.', type: 'number'},
    {key: 'revenue_post_returns', label: 'Umsatz nach Retouren', type: 'currency'}
  ];

  readonly feesGeneralColumns: TableColumn[] = [
    {key: 'label', label: 'Einzelübersicht Gebühren'},
    {key: 'value', label: 'Basiswert', type: 'currency'}
  ];

  readonly feesReturnsColumns: TableColumn[] = [
    {key: 'label', label: 'Einzelübersicht Retourengebühren'},
    {key: 'value', label: 'Basiswert', type: 'currency'}
  ];

  readonly goodwillRows = computed(() =>
    this.goodwill().sort
      .map(key => (this.goodwill() as any)[key])
      .filter(item => item?.value)
  );

  readonly assortmentRxRows = computed(() =>
    this.assortment().assortment_rx.sort
      .map(key => (this.assortment().assortment_rx as any)[key])
      .filter(item => item?.revenue)
  );

  readonly assortmentNonRxRows = computed(() =>
    this.assortment().assortment_non_rx.sort
      .map(key => (this.assortment().assortment_non_rx as any)[key])
      .filter(item => item?.revenue)
  );

  readonly revenueRows = computed(() => {
    const r = this.revenue();
    return [
      r.rx,
      {...r.of_which_hp, _rowClass: 'row-italic'},
      r.non_rx
    ].filter(item => item?.revenue_pre_returns);
  });

  readonly feesGeneralRows = computed(() =>
    this.fees().fees_general.sort
      .map(key => (this.fees().fees_general as any)[key])
      .filter(item => item?.value)
  );

  readonly feesReturnsRows = computed(() =>
    this.fees().fees_returns.sort
      .map(key => (this.fees().fees_returns as any)[key])
      .filter(item => item?.value)
  );

  readonly goodwillSum = computed(() => ({
    label: 'Summe Kulanzen',
    values: {value: this.goodwill().sum}
  }));

  readonly assortmentRxSum = computed(() => ({
    label: 'Umsatz Servicesortimente RX',
    values: {
      packages: this.assortment().assortment_rx.sums?.packages ?? 0,
      revenue: this.assortment().assortment_rx.sums?.revenue ?? 0
    }
  }));

  readonly assortmentNonRxSum = computed(() => ({
    label: 'Umsatz Servicesortimente Non-RX',
    values: {revenue: this.assortment().assortment_non_rx.sums?.revenue ?? 0}
  }));

  readonly revenueSums = computed(() => {
    const sums = this.revenue().sums;
    return sums ? {
      label: sums.label ?? 'Gesamt',
      values: {
        revenue_pre_returns: sums.revenue_pre_returns ?? 0,
        packages: sums.packages ?? 0,
        revenue_post_returns: sums.revenue_post_returns ?? 0
      }
    } : null;
  });

  readonly feesGeneralSum = computed(() => ({
    label: 'Summe Gebühren',
    values: {value: this.fees().fees_general.sum ?? 0}
  }));

  readonly feesReturnsSum = computed(() => ({
    label: 'Summe Retourengebühren',
    values: {value: this.fees().fees_returns.sum ?? 0}
  }));

  constructor() {
    effect(() => {
      const idf = this.userService.selectedIdf();
      const month = this.selectedMonth();
      if (idf) {
        this.loadEvaluations(idf, month);
      }
    });
  }

  showDetails(detailsId: string): void {
    this.chosenDetails.set(detailsId);
  }

  onMonthSelect(event: MatSelectChange): void {
    this.selectedMonth.set(event.value);
  }

  private loadEvaluations(idf: string, selectedMonth: number): void {
    const {finalYear, finalMonth} = this.calculateEvaluationDate(selectedMonth);

    this.isLoading.set(true);
    this.hasError.set(false);
    this.chosenDetails.set('');

    this.evaluationRestService
      .loadEvaluationData(idf, finalYear, finalMonth)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: ResponseModel<EvaluationData>) => {
          this.evaluationData.set(response.return_object ?? null);
          this.isLoading.set(false);
        },
        error: (err: unknown) => {
          console.error('Error loading evaluations:', err);
          this.evaluationData.set(null);
          this.isLoading.set(false);
          this.hasError.set(true);
        }
      });
  }

  private calculateEvaluationDate(selectedMonth: number): { finalYear: number; finalMonth: number } {
    const today = new Date();
    let baseMonth = today.getMonth();
    const baseYear = today.getFullYear();

    if (today.getDate() < 5) {
      baseMonth--;
    }

    const monthsBack = selectedMonth + 1;
    const resultDate = new Date(baseYear, baseMonth - monthsBack, 1);
    return {
      finalYear: resultDate.getFullYear(),
      finalMonth: resultDate.getMonth() + 1
    };
  }

  private calculateSums(evaluation: EvaluationData | null): EvaluationSum {
    if (!evaluation) {
      return {goodwill: 0, fees: 0, revenue: 0, assortment: 0};
    }
    return {
      goodwill: evaluation.goodwill?.sum ?? 0,
      revenue: evaluation.revenue?.sums?.revenue_pre_returns ?? 0,
      fees: this.calculateFees(evaluation),
      assortment: this.calculateAssortment(evaluation)
    };
  }

  private calculateFees(evaluation: EvaluationData): number {
    return (evaluation.fees_general?.visible ? (evaluation.fees_general.sum ?? 0) : 0)
      + (evaluation.fees_returns?.visible ? (evaluation.fees_returns.sum ?? 0) : 0);
  }

  private calculateAssortment(evaluation: EvaluationData): number {
    return (evaluation.assortment_non_rx?.visible ? (evaluation.assortment_non_rx.sums?.revenue ?? 0) : 0)
      + (evaluation.assortment_rx?.visible ? (evaluation.assortment_rx.sums?.revenue ?? 0) : 0);
  }
}
